import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import HistoryIcon from '@mui/icons-material/History'
import API from '../../../../services/API.js'
import { EmployeeContext } from './employeeSingle'
import { GlobalContext } from '../../../../App'
import MandatoryTrainingPopup from './MandatoryTrainingPopup.jsx'
import MandatoryTrainingRecordDialog from './MandatoryTrainingRecordDialog.jsx'
import {
  getCourseStatus,
  getExpiryDate,
  isLegacyELearning,
  STATUS,
} from '../../../../helpers/mandatoryTrainingStatus'
import { getCatalogue } from '../../../../training/helpers/trainingApi'
import {
  catalogueCourseFor,
  requiredPathwayTitle,
} from '../../../../training/components/admin/catalogueHelpers'
import EmployeeELearningSection from '../../../../training/components/admin/EmployeeELearningSection'

const SUMMARY_ORDER = ['compliant', 'expiring', 'overdue', 'notCompleted', 'pending']

const MandatoryTrainingTab = ({ employee }) => {
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  // E-learning catalogue, for pathway titles. Optional: empty below Premium
  const [catalogueCourses, setCatalogueCourses] = useState([])
  const [historyCourse, setHistoryCourse] = useState(null)
  // { course, record } - record set = edit, course set = preselected add
  const [dialog, setDialog] = useState(null)

  const { loadEmployee, setOpenTab } = useContext(EmployeeContext)
  const { setToast } = useContext(GlobalContext)

  const clinicalRole = employee?.employee?.clinicalRole
  const records = employee?.employee?.mandatoryTraining ?? []

  useEffect(() => {
    API.get('/mandatorytraininglist')
      .then((res) => {
        if (res.status === 200) setCourses(res.data)
      })
      .finally(() => setLoading(false))
    getCatalogue()
      .then((res) => {
        if (res.status === 200 && res.data?.available) {
          setCatalogueCourses(res.data.courses ?? [])
        }
      })
      .catch(() => {})
  }, [])

  const { requiredRows, otherRows, summary } = useMemo(() => {
    const required = []
    const other = []
    const counts = {}
    courses.forEach((course) => {
      const status = getCourseStatus(course, records)
      const requirement = course.requiredBy?.[clinicalRole]
      const mine = records.filter(
        (record) => String(record.course?._id ?? record.course) === String(course._id),
      )
      const row = {
        course,
        ...status,
        // requiredBy holds true or the id of the pathway the role must do
        pathway: requirement
          ? requiredPathwayTitle(
              requirement,
              catalogueCourseFor(catalogueCourses, course.catalogueCode),
            )
          : null,
        // Results from the retired e-learning stay as history only
        legacyOnly: !status.latest && mine.some(isLegacyELearning),
      }
      if (requirement) {
        required.push(row)
        counts[status.key] = (counts[status.key] ?? 0) + 1
      } else if (status.latest) {
        other.push(row)
      } else if (row.legacyOnly) {
        // Keeps the history reachable without calling the course incomplete
        other.push({ ...row, ...STATUS.legacy })
      }
    })
    return { requiredRows: required, otherRows: other, summary: counts }
  }, [courses, records, clinicalRole, catalogueCourses])

  const handleSaved = () => {
    setDialog(null)
    setHistoryCourse(null)
    setToast({ message: 'Mandatory Training Updated' })
    loadEmployee()
    setOpenTab(1)
  }

  const handleDeleted = () => {
    setHistoryCourse(null)
    setToast({ message: 'Training Record Deleted' })
    loadEmployee()
    setOpenTab(1)
  }

  const CourseRow = ({ row, muted = false }) => {
    const { course, latest, label, color, pathway, legacyOnly } = row
    const expiry = latest && !latest.requiresApproval ? getExpiryDate(course, latest) : null
    return (
      <TableRow
        hover
        sx={{ cursor: 'pointer', opacity: muted ? 0.75 : 1 }}
        onClick={() => setHistoryCourse(course)}
      >
        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {course.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {course.frequency ?? 'No renewal frequency set'}
            {pathway ? ` · ${pathway}` : ''}
            {muted ? ' · not required for this role' : ''}
          </Typography>
          {legacyOnly && (
            <Typography variant="caption" color="text.secondary" display="block">
              Earlier e-learning result no longer counts
            </Typography>
          )}
        </TableCell>
        <TableCell align="center">
          <Chip size="small" label={label} color={color} sx={{ borderRadius: 1 }} />
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          {latest ? moment(latest.date).format('DD MMM YYYY') : '—'}
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
          {expiry ? (
            <Typography
              variant="body2"
              color={expiry.isBefore(moment()) ? 'error.main' : 'text.primary'}
            >
              {expiry.format('DD MMM YYYY')}
            </Typography>
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
          <Stack direction="row" justifyContent="flex-end">
            <Tooltip title="Add a record for this course">
              <IconButton
                size="small"
                color="third"
                onClick={() => setDialog({ course, record: null })}
                aria-label={`Add ${course.title} record`}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="View history">
              <IconButton
                size="small"
                onClick={() => setHistoryCourse(course)}
                aria-label={`${course.title} history`}
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
    )
  }

  const Header = () => (
    <TableHead>
      <TableRow>
        <TableCell>
          <b>Course</b>
        </TableCell>
        <TableCell align="center">
          <b>Status</b>
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <b>Last Completed</b>
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
          <b>Valid Until</b>
        </TableCell>
        <TableCell />
      </TableRow>
    </TableHead>
  )

  if (loading) return <LinearProgress />

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={1}
      >
        <Stack direction="row" gap={1} flexWrap="wrap">
          {SUMMARY_ORDER.filter((key) => summary[key]).map((key) => (
            <Chip
              key={key}
              size="small"
              variant="outlined"
              color={STATUS[key].color}
              label={`${summary[key]} ${STATUS[key].label}`}
              sx={{ borderRadius: 1 }}
            />
          ))}
          {requiredRows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {clinicalRole
                ? `No courses are marked as required for ${clinicalRole}. Set required roles per course in Configuration → Mandatory Training.`
                : 'This person has no clinical role set, so no courses are required.'}
            </Typography>
          )}
        </Stack>
        <Button
          variant="contained"
          color="third"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setDialog({ course: null, record: null })}
          disabled={courses.length === 0}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Add Training Record
        </Button>
      </Stack>

      {requiredRows.length > 0 && (
        <Box overflow="auto">
          <Table size="small">
            <Header />
            <TableBody>
              {requiredRows.map((row) => (
                <CourseRow key={row.course._id} row={row} />
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {otherRows.length > 0 && (
        <Stack gap={1}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            Other recorded training (not required for this role)
          </Typography>
          <Box overflow="auto">
            <Table size="small">
              <Header />
              <TableBody>
                {otherRows.map((row) => (
                  <CourseRow key={row.course._id} row={row} muted />
                ))}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      )}

      <MandatoryTrainingPopup
        selectedCourse={historyCourse}
        setSelectedCourse={setHistoryCourse}
        employee={employee}
        catalogueCourses={catalogueCourses}
        onAddRecord={(course) => setDialog({ course, record: null })}
        onEditRecord={(course, record) => setDialog({ course, record })}
        onDeleted={handleDeleted}
      />

      <MandatoryTrainingRecordDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        employee={employee}
        courses={courses}
        course={dialog?.course ?? null}
        record={dialog?.record ?? null}
        onSaved={handleSaved}
      />

      <EmployeeELearningSection
        employeeId={employee?.employee?._id}
        employeeName={[employee?.firstName, employee?.lastName].filter(Boolean).join(' ')}
        onRecordsChanged={() => {
          loadEmployee()
          setOpenTab(1)
        }}
      />
    </Stack>
  )
}

export default MandatoryTrainingTab
